<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {storeToRefs} from 'pinia'
import {Pencil, Plus, Trash2} from 'lucide-vue-next'
import {Language, type Profile, type ProfileSettings, type SavedProfile} from '@shared/types'
import {profileApi} from '@/api'
import {useLocalStore} from '@/stores/local'
import {useUserStore} from '@/stores/user'
import {getRepresentations} from '@/lib/transliteration'
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert'
import {Switch} from '@/components/ui/switch'
import {Button} from '@/components/ui/button'
import {Card, CardTitle} from '@/components/ui/card'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import i18next from 'i18next'

type FormMode = 'create' | 'edit' | null

const targetFlagCountries: Record<Language, string> = {
  [Language.English]: 'us',
  [Language.Russian]: 'ru',
  [Language.German]: 'de',
  [Language.Italian]: 'it',
  [Language.Polish]: 'pl',
  [Language.Armenian]: 'am',
}

const userStore = useUserStore()
const localStore = useLocalStore()
const {profile} = storeToRefs(userStore)

const profiles = ref<SavedProfile[]>([])
const formMode = ref<FormMode>(null)
const showForm = ref(false)
const isLoading = ref(true)
const isSaving = ref(false)
const errorMessage = ref('')
const currentProfileId = ref<number | null>(null)
const transliterations = ref<string[]>([])
const transliterationEnabled = ref(false)

const emptyProfile = (): Profile => ({
  settings: {
    native_language: Language.Russian,
    target_language: Language.English,
    transliterations: [],
  },
})

const currentProfile = ref<Profile>(emptyProfile())
const activeProfileId = computed(() => profile.value?.profile_id ?? localStore.profileId)
const targetLanguages = [Language.English, Language.German, Language.Italian, Language.Polish, Language.Armenian]
const interfaceLanguages = [Language.English, Language.Russian]
const nativeLanguages = computed(() =>
  interfaceLanguages.filter((language) => language !== currentProfile.value.settings.target_language)
)
const availableRepresentations = computed(() => getRepresentations(currentProfile.value.settings.target_language))

function languageLabel(language: Language): string {
  return i18next.t(`common.${language}`)
}

function profileTitle(item: SavedProfile): string {
  return i18next.t('profile.pair', {tl: item.settings.target_language, nl: item.settings.native_language})
}

function onTransliterationToggle(checked: boolean): void {
  transliterationEnabled.value = checked
  if (!checked) {
    transliterations.value = []
  }
}

function toggleRepresentation(id: string, checked: boolean): void {
  if (checked) {
    if (!transliterations.value.includes(id)) {
      transliterations.value.push(id)
    }
  } else {
    transliterations.value = transliterations.value.filter((item) => item !== id)
  }
}

async function syncDefaultProfile(): Promise<void> {
  if (!profiles.value.length) {
    localStore.profileId = NaN
    return
  }

  const savedProfile = profiles.value.find((item) => item.profile_id === localStore.profileId) ?? profiles.value[0]
  await userStore.selectProfile(savedProfile.profile_id)
}

async function fetchProfiles(): Promise<void> {
  try {
    errorMessage.value = ''
    profiles.value = await profileApi.list()
    await syncDefaultProfile()
  } catch {
    errorMessage.value = i18next.t('profile.loadError')
  } finally {
    isLoading.value = false
  }
}

function editProfile(item: SavedProfile): void {
  currentProfile.value = {...item}
  currentProfileId.value = item.profile_id
  transliterations.value = item.settings.transliterations ?? []
  transliterationEnabled.value = transliterations.value.length > 0
  formMode.value = 'edit'
  showForm.value = true
}

function startCreate(): void {
  currentProfile.value = emptyProfile()
  currentProfileId.value = null
  transliterations.value = []
  transliterationEnabled.value = false
  formMode.value = 'create'
  showForm.value = true
}

function cancelEdit(): void {
  formMode.value = null
  showForm.value = false
  currentProfileId.value = null
  currentProfile.value = emptyProfile()
  transliterations.value = []
  transliterationEnabled.value = false
}

async function saveProfile(): Promise<void> {
  if (!formMode.value) return

  try {
    isSaving.value = true
    errorMessage.value = ''
    const settings: ProfileSettings = {
      ...currentProfile.value.settings,
      transliterations: transliterations.value,
    }

    if (formMode.value === 'edit' && currentProfileId.value) {
      await profileApi.update(currentProfileId.value, {settings})
    } else {
      const created = await profileApi.create(settings)
      localStore.profileId = created.profile_id
    }

    cancelEdit()
    await fetchProfiles()
  } catch {
    errorMessage.value = i18next.t('profile.saveError')
  } finally {
    isSaving.value = false
  }
}

async function setDefaultProfile(profileId: number): Promise<void> {
  await userStore.selectProfile(profileId)
}

function activateProfile(item: SavedProfile): void {
  if (item.profile_id === activeProfileId.value) return
  void setDefaultProfile(item.profile_id)
}

async function deleteProfile(profileId: number): Promise<void> {
  if (!window.confirm(i18next.t('profile.deleteConfirm'))) return

  try {
    await profileApi.remove(profileId)
    if (activeProfileId.value === profileId) {
      localStore.profileId = NaN
    }
    await fetchProfiles()
  } catch {
    errorMessage.value = i18next.t('profile.deleteError')
  }
}

onMounted(() => {
  void fetchProfiles()
})
</script>

<template>
  <Teleport defer to="#app-header-left">
    <h1 class="text-sm font-medium">{{ $t('profile.title') }}</h1>
  </Teleport>
  <Teleport defer to="#app-header-right">
    <Button type="button" @click="startCreate"><Plus class="mr-2 h-4 w-4" />{{ $t('profile.createNew') }}</Button>
  </Teleport>
  <div class="mx-auto w-full max-w-3xl space-y-6 p-4 md:p-8">
    <Alert v-if="errorMessage" variant="destructive">
      <AlertTitle>{{ $t('common.error') }}</AlertTitle>
      <AlertDescription>{{ errorMessage }}</AlertDescription>
    </Alert>

    <div v-if="isLoading" class="rounded-lg border p-8 text-center text-muted-foreground">
      {{ $t('common.loading') }}...
    </div>
    <div v-else-if="!profiles.length" class="rounded-lg border border-dashed p-10 text-center">
      <h2 class="text-lg font-medium">{{ $t('profile.emptyTitle') }}</h2>
      <p class="mt-1 text-sm text-muted-foreground">{{ $t('profile.emptyDescription') }}</p>
      <Button class="mt-5" type="button" @click="startCreate"
        ><Plus class="mr-2 h-4 w-4" />{{ $t('profile.createNew') }}</Button
      >
    </div>

    <div v-else class="flex flex-col gap-3">
      <Card
        v-for="item in profiles"
        :key="item.profile_id"
        class="flex items-center gap-3 px-4 py-3"
        :class="item.profile_id === activeProfileId ? 'border-primary/60' : ''">
        <Switch
          :model-value="item.profile_id === activeProfileId"
          :aria-label="profileTitle(item)"
          @update:model-value="(checked: boolean) => checked && activateProfile(item)" />
        <CircleFlags
          class="shrink-0"
          size="small"
          :country="targetFlagCountries[item.settings.target_language]"
          :show-flag-name="false" />
        <CardTitle class="min-w-0 flex-1 text-base leading-snug first-letter:uppercase">{{
          profileTitle(item)
        }}</CardTitle>
        <TooltipProvider :delay-duration="200">
          <div class="flex shrink-0 items-center gap-1">
            <Tooltip>
              <TooltipTrigger as-child>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  type="button"
                  :aria-label="$t('profile.actions.edit')"
                  @click="editProfile(item)">
                  <Pencil class="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{{ $t('profile.actions.edit') }}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger as-child>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  class="text-destructive hover:text-destructive"
                  type="button"
                  :aria-label="$t('profile.actions.delete')"
                  @click="deleteProfile(item.profile_id)">
                  <Trash2 class="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{{ $t('profile.actions.delete') }}</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </Card>
    </div>
  </div>

  <Dialog v-model:open="showForm">
    <DialogContent class="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{{ formMode === 'edit' ? $t('profile.edit') : $t('profile.create') }}</DialogTitle>
        <DialogDescription>{{ $t('profile.formDescription') }}</DialogDescription>
      </DialogHeader>
      <form class="grid gap-6 md:grid-cols-2" @submit.prevent="saveProfile">
        <div class="grid gap-2">
          <Label for="target-language">{{ $t('profile.targetLanguage') }}</Label
          ><Select v-model="currentProfile.settings.target_language"
            ><SelectTrigger id="target-language"><SelectValue /></SelectTrigger
            ><SelectContent
              ><SelectItem v-for="language in targetLanguages" :key="language" :value="language">{{
                languageLabel(language)
              }}</SelectItem></SelectContent
            ></Select
          >
        </div>
        <div class="grid gap-2">
          <Label for="native-language">{{ $t('profile.nativeLanguage') }}</Label
          ><Select v-model="currentProfile.settings.native_language"
            ><SelectTrigger id="native-language"><SelectValue /></SelectTrigger
            ><SelectContent
              ><SelectItem v-for="language in nativeLanguages" :key="language" :value="language">{{
                languageLabel(language)
              }}</SelectItem></SelectContent
            ></Select
          >
        </div>
        <div v-if="availableRepresentations.length" class="md:col-span-2 grid gap-4 rounded-md border p-4">
          <div class="space-y-1">
            <h3 class="font-medium">{{ $t('transliteration.title') }}</h3>
            <p class="text-sm text-muted-foreground">{{ $t('transliteration.description') }}</p>
          </div>
          <div class="flex items-center justify-between gap-4">
            <Label>{{ $t('transliteration.enable') }}</Label>
            <Switch
              :model-value="transliterationEnabled"
              :aria-label="$t('transliteration.enable')"
              @update:model-value="onTransliterationToggle" />
          </div>
          <div v-if="transliterationEnabled" class="space-y-3">
            <p class="text-sm font-medium text-muted-foreground">{{ $t('transliteration.representations') }}</p>
            <div
              v-for="representation in availableRepresentations"
              :key="representation.id"
              class="flex items-center justify-between gap-4">
              <Label>{{ $t(representation.labelKey) }}</Label>
              <Switch
                :model-value="transliterations.includes(representation.id)"
                :aria-label="$t(representation.labelKey)"
                @update:model-value="(checked: boolean) => toggleRepresentation(representation.id, checked)" />
            </div>
          </div>
        </div>
        <div class="md:col-span-2 grid gap-3 rounded-md border p-4">
          <div class="space-y-1">
            <h3 class="font-medium">{{ $t('profile.emailDigest') }}</h3>
            <p class="text-sm text-muted-foreground">{{ $t('profile.emailDigestDescription') }}</p>
          </div>
          <div class="flex items-center justify-between gap-4">
            <Label for="profile-email-digest">{{ $t('profile.emailDigest') }}</Label>
            <Switch
              id="profile-email-digest"
              data-testid="profile-email-digest"
              :model-value="currentProfile.settings.email_digest_enabled === true"
              :aria-label="$t('profile.emailDigest')"
              @update:model-value="(checked: boolean) => (currentProfile.settings.email_digest_enabled = checked)" />
          </div>
        </div>
        <DialogFooter class="md:col-span-2">
          <Button variant="outline" type="button" @click="cancelEdit">{{ $t('profile.actions.cancel') }}</Button>
          <Button type="submit" :disabled="isSaving">
            {{
              isSaving
                ? $t('common.saving')
                : formMode === 'edit'
                  ? $t('profile.actions.update')
                  : $t('profile.actions.create')
            }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
